import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { NEET_PHYSICS_CH10_QUESTIONS } from "../data/neetPhysicsCh10Questions";

// ─────────────────────────────────────────────────────────────────────────────
// NEET Physics — Chapter 10: Mechanical Properties of Fluids (NCERT Class 11).
// Content is drafted from the NCERT syllabus topic list; each concept has an
// explanation plus a worked example. NOT YET human-reviewed by a subject-matter
// expert — treat as a first draft and verify against NCERT before exam use.
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS = ["Pressure & Pascal's Law", "Buoyancy & Archimedes' Principle", "Fluid Flow — Continuity & Bernoulli", "Viscosity & Surface Tension"];

const SECTION_COLORS = {
  "Pressure & Pascal's Law":               "#2563eb",
  "Buoyancy & Archimedes' Principle":      "#7c3aed",
  "Fluid Flow — Continuity & Bernoulli":   "#dc2626",
  "Viscosity & Surface Tension":           "#16a34a",
};

const CONCEPTS = [
  // ── Pressure & Pascal's Law ─────────────────────────────────────────────────
  { section: "Pressure & Pascal's Law", title: "Pressure in a fluid and its variation with depth",
    explanation: "Pressure is defined as normal force per unit area, P = F/A, with SI unit pascal (Pa = N/m2). At a point inside a fluid at rest, pressure acts equally in all directions (it is a scalar, not a vector). Pressure increases with depth below a free surface according to P = P0 + rho.g.h, where P0 is the pressure at the surface, rho is the fluid's density, and h is the depth -- deeper fluid must support the weight of everything above it.",
    example: `P = P0 + rho.g.h

Water tank, depth h = 10 m, rho = 1000 kg/m^3, g = 9.8 m/s^2
Gauge pressure due to water column:
rho.g.h = 1000 x 9.8 x 10 = 9.8 x 10^4 Pa

Absolute pressure at that depth (P0 = 1.013 x 10^5 Pa, atmospheric):
P = 1.013x10^5 + 0.98x10^5 = 1.99 x 10^5 Pa` },
  { section: "Pressure & Pascal's Law", title: "Pascal's law and the hydraulic lift/press",
    explanation: "Pascal's law states that pressure applied to an enclosed, incompressible fluid is transmitted undiminished to every point of the fluid and to the walls of the container. In a hydraulic lift, a small force F1 on a small piston (area A1) creates pressure P = F1/A1, which is transmitted to a larger piston (area A2), producing a much larger output force F2 = P.A2 = F1.(A2/A1) -- this is how a hydraulic press/lift/brake multiplies force.",
    example: `P = F1/A1 = F2/A2   ->   F2 = F1 x (A2/A1)

Small piston: A1 = 1 cm^2 = 1x10^-4 m^2, F1 = 10 N
Pressure created: P = 10 / 1x10^-4 = 1x10^5 Pa

Large piston: A2 = 100 cm^2 = 1x10^-2 m^2
F2 = P x A2 = 1x10^5 x 1x10^-2 = 1000 N

(100x force multiplication = ratio of piston areas A2/A1)` },
  { section: "Pressure & Pascal's Law", title: "Atmospheric pressure and the mercury barometer",
    explanation: "Atmospheric pressure is the pressure exerted by the weight of the air column above a point, about 1.013x10^5 Pa (1 atm) at sea level. It is measured with a mercury barometer: a vertical tube filled with mercury, inverted in a mercury reservoir, with a near-vacuum above the mercury column. The atmosphere supports a column of height h, so P0 = rho_Hg . g . h.",
    example: `P0 = rho_Hg . g . h

Standard barometric height: h = 0.76 m (760 mm Hg)
rho_Hg = 13600 kg/m^3, g = 9.8 m/s^2

P0 = 13600 x 9.8 x 0.76 = 1.013 x 10^5 Pa  (= 1 atm)` },
  { section: "Pressure & Pascal's Law", title: "Gauge pressure vs absolute pressure",
    explanation: "Absolute pressure is the total pressure at a point, measured from a perfect vacuum. Gauge pressure is the pressure measured relative to atmospheric pressure -- it is what most practical pressure gauges (tire gauges, blood pressure monitors) actually read. Absolute pressure = atmospheric pressure + gauge pressure; gauge pressure can be negative (below atmospheric, e.g. a vacuum gauge).",
    example: `P_absolute = P_atm + P_gauge

Tire pressure gauge reads: P_gauge = 200 kPa
Atmospheric pressure: P_atm = 101.3 kPa

P_absolute = 200 + 101.3 = 301.3 kPa` },

  // ── Buoyancy & Archimedes' Principle ────────────────────────────────────────
  { section: "Buoyancy & Archimedes' Principle", title: "Archimedes' principle and buoyant force",
    explanation: "Archimedes' principle states that a body fully or partially submerged in a fluid experiences an upward buoyant force equal to the weight of the fluid it displaces: F_B = rho_fluid . V_displaced . g. This buoyant force arises because fluid pressure increases with depth, so the upward pressure force on the bottom of the submerged body exceeds the downward pressure force on its top.",
    example: `F_B = rho_fluid x V_displaced x g

Object fully submerged in water, V_displaced = 2x10^-3 m^3
rho_water = 1000 kg/m^3, g = 9.8 m/s^2

F_B = 1000 x 2x10^-3 x 9.8 = 19.6 N` },
  { section: "Buoyancy & Archimedes' Principle", title: "Conditions for floating and sinking",
    explanation: "Comparing a body's average density to the fluid's density predicts its behaviour: if rho_body < rho_fluid, the body floats with part of it above the surface; if rho_body > rho_fluid, the buoyant force at full submersion is still less than its weight, so it sinks; if rho_body = rho_fluid, the body is in neutral equilibrium and stays suspended at whatever depth it is placed (e.g. a submarine adjusting its ballast).",
    example: `Compare rho_body to rho_water (1000 kg/m^3):

rho_iron = 7800 kg/m^3  > 1000  -> sinks
rho_cork = 240 kg/m^3   < 1000  -> floats
rho_body = rho_fluid            -> neutrally buoyant (stays suspended)` },
  { section: "Buoyancy & Archimedes' Principle", title: "Law of flotation and degree of immersion",
    explanation: "A floating body is in equilibrium when its weight equals the buoyant force of the fluid it displaces: rho_body . V . g = rho_fluid . V_submerged . g. This gives the submerged fraction V_submerged/V = rho_body/rho_fluid -- a denser (but still lighter-than-fluid) floating object rides lower in the water, submerging a larger fraction of its volume.",
    example: `V_submerged / V_total = rho_body / rho_fluid

Ice floating in water: rho_ice = 900 kg/m^3, rho_water = 1000 kg/m^3

Fraction submerged = 900/1000 = 0.90  ->  90% of the ice is
underwater, only about 10% shows above the surface` },
  { section: "Buoyancy & Archimedes' Principle", title: "Apparent weight loss in a fluid",
    explanation: "A submerged object appears lighter because the buoyant force partly supports its true weight: apparent weight = actual weight - buoyant force. This 'weight loss' equals the weight of the fluid displaced, and is the basis of methods to find a solid's density or relative density (specific gravity) using a spring balance in and out of a liquid.",
    example: `Apparent weight = W_actual - F_B

Stone: mass m = 0.5 kg, rho_stone = 2500 kg/m^3
W_actual = mg = 0.5 x 9.8 = 4.9 N

Volume of stone: V = m/rho = 0.5/2500 = 2x10^-4 m^3
Buoyant force in water: F_B = 1000 x 2x10^-4 x 9.8 = 1.96 N

Apparent weight = 4.9 - 1.96 = 2.94 N` },

  // ── Fluid Flow — Continuity & Bernoulli ─────────────────────────────────────
  { section: "Fluid Flow — Continuity & Bernoulli", title: "Streamline (laminar) vs turbulent flow, Reynolds number",
    explanation: "In streamline (laminar) flow, fluid particles move along smooth, well-defined paths (streamlines) that never cross, with velocity at any fixed point constant in time; layers slide past each other without mixing. Beyond a critical speed, flow becomes turbulent -- chaotic, with eddies and mixing between layers. The Reynolds number Re = rho.v.d/eta (a dimensionless quantity) predicts the transition: flow is generally streamline for Re below about 1000-2000, and turbulent above about 3000.",
    example: `Re = rho.v.d / eta

Water in a pipe: rho = 1000 kg/m^3, v = 0.5 m/s, d = 0.02 m,
eta (water) = 1x10^-3 Pa.s

Re = (1000 x 0.5 x 0.02) / 1x10^-3 = 10 / 0.001 = 10,000

Re = 10,000 >> 3000  ->  flow is turbulent, not streamline` },
  { section: "Fluid Flow — Continuity & Bernoulli", title: "Equation of continuity: A1v1 = A2v2",
    explanation: "For an incompressible fluid in steady flow through a pipe of varying cross-section, mass is conserved, so the volume flow rate (A.v) must be the same at every cross-section: A1.v1 = A2.v2. This means fluid speeds up where the pipe narrows and slows down where it widens -- a direct consequence of conservation of mass, with no fluid created or destroyed anywhere along the tube.",
    example: `A1.v1 = A2.v2

Wide section: A1 = 4 cm^2, v1 = 2 m/s
Narrow section: A2 = 1 cm^2

v2 = A1.v1 / A2 = (4 x 2) / 1 = 8 m/s

(Area drops to 1/4 -> speed rises 4x, keeping A.v constant)` },
  { section: "Fluid Flow — Continuity & Bernoulli", title: "Bernoulli's principle and Bernoulli's equation",
    explanation: "Bernoulli's equation applies the work-energy theorem to an ideal (non-viscous, incompressible), steady, streamline flow: P + (1/2)rho.v^2 + rho.g.h = constant along a streamline. It expresses that pressure energy, kinetic energy, and potential energy per unit volume sum to a constant -- so wherever a fluid's speed is higher (at constant height), its pressure must be lower, and vice versa.",
    example: `P1 + (1/2)rho.v1^2 = P2 + (1/2)rho.v2^2   (horizontal pipe, h const)

Using the continuity example: v1 = 2 m/s, v2 = 8 m/s, rho = 1000

P1 - P2 = (1/2) x 1000 x (8^2 - 2^2) = 500 x (64-4) = 500 x 60
P1 - P2 = 30,000 Pa = 3x10^4 Pa

(pressure is 30 kPa LOWER in the faster, narrower section)` },
  { section: "Fluid Flow — Continuity & Bernoulli", title: "Venturi meter and speed of efflux (Torricelli's law)",
    explanation: "A venturi meter measures flow speed by narrowing a horizontal pipe and measuring the resulting pressure drop, using Bernoulli's equation together with continuity. A related application is the speed of efflux: liquid escaping through a small hole at depth h below the open surface of a large tank (both points open to the same atmospheric pressure) leaves with speed v = sqrt(2gh) -- Torricelli's law, identical in form to a body freely falling through height h.",
    example: `Venturi meter (horizontal): A1=5 cm^2, A2=2 cm^2, v1=1 m/s
Continuity: v2 = A1v1/A2 = 5x1/2 = 2.5 m/s
Bernoulli: P1-P2 = (1/2)rho(v2^2-v1^2) = 500(6.25-1) = 2625 Pa
(this measurable pressure drop gives the flow speed/rate)

Torricelli's law: v = sqrt(2gh)
Hole at depth h = 5 m below the free surface:
v = sqrt(2 x 9.8 x 5) = sqrt(98) = 9.9 m/s` },
  { section: "Fluid Flow — Continuity & Bernoulli", title: "Atomizer and dynamic lift on an aircraft wing",
    explanation: "Both effects follow from Bernoulli's principle: where fluid speed is higher, pressure is lower. In an atomizer (perfume spray, carburettor), a fast air jet blown across the top of a vertical tube lowers the pressure there; atmospheric pressure on the liquid below then pushes it up the tube, where the airstream breaks it into a fine spray. Over an aerofoil-shaped wing, air flows faster over the curved upper surface than beneath the flatter lower surface, so pressure above is lower than pressure below; this pressure difference produces a net upward force called dynamic lift.",
    example: `Atomizer: fast air over tube top -> low pressure there ->
atmospheric pressure pushes liquid up the tube -> fine spray

Aircraft wing (simplified):
  air above wing: faster  -> lower pressure  (P_above)
  air below wing: slower  -> higher pressure (P_below)
  Dynamic lift ~ (P_below - P_above) x wing area   (net upward)` },

  // ── Viscosity & Surface Tension ─────────────────────────────────────────────
  { section: "Viscosity & Surface Tension", title: "Viscosity and Newton's law of viscous force",
    explanation: "Viscosity is a fluid's internal resistance to relative motion between its layers -- effectively friction between fluid layers moving at different speeds. Newton's law of viscous flow states the tangential (viscous) force between adjacent layers is F = eta.A.(dv/dx), where A is the contact area, dv/dx is the velocity gradient perpendicular to flow, and eta is the coefficient of viscosity (SI unit: Pa.s, also called the poiseuille; 1 poise = 0.1 Pa.s).",
    example: `F = eta . A . (dv/dx)

Water layer: eta = 1x10^-3 Pa.s, A = 0.02 m^2,
velocity gradient dv/dx = 5 s^-1

F = 1x10^-3 x 0.02 x 5 = 1x10^-4 N` },
  { section: "Viscosity & Surface Tension", title: "Stokes' law and viscous drag on a sphere",
    explanation: "Stokes' law gives the viscous drag force on a small sphere moving slowly (streamline/low Reynolds number conditions) through a viscous fluid: F = 6.pi.eta.r.v, where r is the sphere's radius, v its speed relative to the fluid, and eta the fluid's coefficient of viscosity. The drag force is directed opposite to the sphere's motion and increases linearly with both its radius and speed.",
    example: `F = 6.pi.eta.r.v

Small sphere: r = 1x10^-3 m, v = 0.02 m/s,
fluid viscosity eta = 1.0 Pa.s (e.g. glycerine)

F = 6 x pi x 1.0 x 1x10^-3 x 0.02
F = 6 x 3.1416 x 2x10^-5 ~ 3.77x10^-4 N` },
  { section: "Viscosity & Surface Tension", title: "Terminal velocity of a sphere falling through a viscous fluid",
    explanation: "A sphere falling through a viscous fluid speeds up until the net force is zero: weight is balanced by the (upward) buoyant force plus the (upward, speed-dependent) Stokes' drag. Beyond this point acceleration is zero and the sphere falls at a constant terminal velocity: (4/3)pi.r^3.rho.g = (4/3)pi.r^3.sigma.g + 6.pi.eta.r.v_t, which rearranges to v_t = 2r^2.g.(rho - sigma) / (9.eta), where rho is the sphere's density and sigma the fluid's density.",
    example: `v_t = 2.r^2.g.(rho - sigma) / (9.eta)

Sphere: r = 1x10^-3 m, rho = 8000 kg/m^3 (dense sphere)
Fluid: sigma = 1000 kg/m^3, eta = 1.0 Pa.s, g = 9.8 m/s^2

v_t = 2 x (1x10^-3)^2 x 9.8 x (8000-1000) / (9 x 1.0)
v_t = 2 x 1x10^-6 x 9.8 x 7000 / 9
v_t = 0.1372 / 9 ~ 0.0152 m/s  (~1.52 cm/s)` },
  { section: "Viscosity & Surface Tension", title: "Surface tension and surface energy",
    explanation: "Surface tension T is the force per unit length acting along any line in a liquid's surface, tending to minimise surface area (SI unit: N/m). Equivalently, T equals the surface energy per unit area -- the work needed to increase a liquid surface's area by one unit, since molecules brought to the surface must overcome the inward pull of neighbouring molecules. A film (like a soap film) has TWO free surfaces, so forces and energies for a film are double those for a single liquid surface.",
    example: `Wire frame with a sliding wire of length l = 0.1 m, holding a
soap film (2 surfaces), T = 0.03 N/m

Force needed to hold the wire in place: F = 2.T.l
F = 2 x 0.03 x 0.1 = 6x10^-3 N

If the film's area increases so each surface grows by 0.001 m^2:
Work done = T x (total area increase) = 0.03 x 0.002 = 6x10^-5 J` },
  { section: "Viscosity & Surface Tension", title: "Angle of contact and capillary rise",
    explanation: "The angle of contact is the angle between the liquid surface (meniscus) and the solid surface at the point of contact, measured inside the liquid; it is small (~0 deg) for a liquid that wets the solid (water-glass) and obtuse (>90 deg) for one that doesn't (mercury-glass), which is why mercury is DEPRESSED rather than raised in a capillary. For a wetting liquid, capillary rise in a fine tube of radius r is h = 2.T.cos(theta) / (rho.g.r) -- the narrower the tube, the higher the liquid rises.",
    example: `h = 2.T.cos(theta) / (rho.g.r)

Water in a glass capillary: T = 0.072 N/m, theta ~ 0 deg (cos=1)
rho = 1000 kg/m^3, g = 9.8 m/s^2, r = 2x10^-4 m (0.2 mm)

h = (2 x 0.072 x 1) / (1000 x 9.8 x 2x10^-4)
h = 0.144 / 1.96 = 0.0735 m ~ 7.35 cm` },
];

const S = {
  page:  { minHeight:"100vh", background:"linear-gradient(135deg,#0f172a,#1a1a2e)", fontFamily:"'Segoe UI',sans-serif", color:"#e2e8f0", padding:"18px 16px 80px",
    userSelect:"none", WebkitUserSelect:"none", MozUserSelect:"none", msUserSelect:"none" },
  back:  { color:"#94a3b8", fontSize:13, textDecoration:"none" },
  card:  { background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.11)", borderRadius:14, padding:"18px", marginBottom:16 },
  chip:  (active, color) => ({
    background: active ? color : "rgba(255,255,255,0.08)",
    color: active ? "#fff" : "#94a3b8",
    border:"none", borderRadius:20, padding:"6px 14px", fontSize:12.5, fontWeight:700, cursor:"pointer", marginRight:8, marginBottom:8,
  }),
  tag: (color) => ({ background:color, color:"#fff", borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, display:"inline-block" }),
  pre: { background:"#0b1120", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"12px 14px", fontSize:12.5, lineHeight:1.6, color:"#a5f3b8", overflowX:"auto", fontFamily:"'Fira Code','Consolas',monospace", whiteSpace:"pre" },
  option: (bg) => ({ display:"block", width:"100%", textAlign:"left", background:bg, border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"9px 12px", color:"#e2e8f0", fontSize:13, marginBottom:7, cursor:"pointer" }),
  iconBtn: (bg) => ({ background:bg, border:"none", borderRadius:8, width:30, height:30, fontSize:14, lineHeight:1, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", flex:"none" }),
};

// ── One interactive objective question — click an option to check it ──────────
function QuizQuestion({ item, index, onAnswer }) {
  const [selected, setSelected] = useState(null);
  const answered = selected !== null;

  const pick = (i) => {
    if (answered) return;
    setSelected(i);
    onAnswer && onAnswer(i === item.correct);
  };

  return (
    <div style={S.card}>
      <span style={S.tag(SECTION_COLORS[item.section])}>{item.section}</span>
      <span style={{ fontSize:11, color:"#64748b", marginLeft:8 }}>{item.topic}</span>
      <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginTop:8, marginBottom:10 }}>{index + 1}. {item.q}</div>
      {item.options.map((opt, i) => {
        let bg = "rgba(255,255,255,0.05)";
        if (answered) {
          if (i === item.correct) bg = "rgba(34,197,94,0.28)";
          else if (i === selected) bg = "rgba(239,68,68,0.28)";
        }
        return (
          <button key={i} style={S.option(bg)} onClick={() => pick(i)} disabled={answered}>
            {String.fromCharCode(65 + i)}. {opt}
          </button>
        );
      })}
      {answered && (
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:8, lineHeight:1.6 }}>
          {selected === item.correct ? "✅ Correct — " : "❌ Not quite — "}{item.explain}
        </div>
      )}
    </div>
  );
}

export default function NeetPhysicsCh10Page() {
  const [mode, setMode] = useState("guide"); // "guide" | "practice"
  const [activeSection, setActiveSection] = useState("All");
  const [score, setScore] = useState({ correct: 0, answered: 0 });

  // ── Bookmarks — same 🔖 pattern used elsewhere on the site, persisted locally ──
  const [bookmarks, setBookmarks] = useState(() => {
    try {
      const raw = window.localStorage.getItem("neetPhysicsCh10Bookmarks");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);

  useEffect(() => {
    try { window.localStorage.setItem("neetPhysicsCh10Bookmarks", JSON.stringify(bookmarks)); } catch { /* ignore */ }
  }, [bookmarks]);

  const toggleBookmark = (title) => {
    setBookmarks(prev => prev.includes(title) ? prev.filter(t => t !== title) : [...prev, title]);
  };

  // ── Read aloud — same speechSynthesis pattern used on Reading Notes / TNPSC ──
  const [speakingIdx, setSpeakingIdx] = useState(null);

  useEffect(() => () => { window.speechSynthesis && window.speechSynthesis.cancel(); }, []);

  const speakConcept = (c, idx) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    if (speakingIdx === idx) { setSpeakingIdx(null); return; }
    const u = new window.SpeechSynthesisUtterance(`${c.title}. ${c.explanation}`);
    u.rate = 0.95;
    u.onend = () => setSpeakingIdx(null);
    u.onerror = () => setSpeakingIdx(null);
    window.speechSynthesis.speak(u);
    setSpeakingIdx(idx);
  };

  // ── Share — native share sheet, falling back to WhatsApp like the other pages ──
  const shareConcept = (c, idx) => {
    const url = `${window.location.origin}${window.location.pathname}#concept-${idx}`;
    const text = `🧪 NEET Physics — ${c.title}\n\n${c.explanation}\n\n${url}`;
    if (navigator.share) {
      navigator.share({ title: `NEET Physics — ${c.title}`, text, url }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    }
  };

  const filtered = useMemo(() => {
    let list = activeSection === "All" ? CONCEPTS : CONCEPTS.filter(c => c.section === activeSection);
    if (showBookmarkedOnly) list = list.filter(c => bookmarks.includes(c.title));
    return list;
  }, [activeSection, showBookmarkedOnly, bookmarks]);

  const filteredQuestions = useMemo(() => (
    activeSection === "All" ? NEET_PHYSICS_CH10_QUESTIONS : NEET_PHYSICS_CH10_QUESTIONS.filter(q => q.section === activeSection)
  ), [activeSection]);

  const handleModeChange = (m) => {
    setMode(m);
    setScore({ correct: 0, answered: 0 });
  };

  const handleAnswer = (isCorrect) => {
    setScore(s => ({ correct: s.correct + (isCorrect ? 1 : 0), answered: s.answered + 1 }));
  };

  return (
    <div
      style={S.page}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <Link to="/neet-physics" style={S.back}>← Back to NEET Physics Menu</Link>

      <div style={{ textAlign:"center", padding:"14px 0 10px" }}>
        <div style={{ fontSize:26, fontWeight:800, color:"#fff" }}>🧪 NEET Physics — Chapter 10</div>
        <div style={{ fontSize:15, fontWeight:700, color:"#fbbf24", marginTop:4 }}>Mechanical Properties of Fluids</div>
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:6 }}>
          NCERT Class 11 · {CONCEPTS.length} concepts explained · {NEET_PHYSICS_CH10_QUESTIONS.length} practice questions · NOT YET human-reviewed, verify against NCERT before exam use
        </div>
      </div>

      {/* Guide / Practice mode toggle */}
      <div style={{ textAlign:"center", margin:"14px 0 8px" }}>
        <button style={S.chip(mode==="guide", "#0f766e")} onClick={() => handleModeChange("guide")}>📘 Concept Guide</button>
        <button style={S.chip(mode==="practice", "#b45309")} onClick={() => handleModeChange("practice")}>🎯 Practice Questions ({NEET_PHYSICS_CH10_QUESTIONS.length})</button>
      </div>

      {/* Section filter chips */}
      <div style={{ textAlign:"center", margin:"6px 0 18px" }}>
        <button style={S.chip(activeSection==="All", "#334155")} onClick={() => setActiveSection("All")}>All</button>
        {SECTIONS.map(s => (
          <button key={s} style={S.chip(activeSection===s, SECTION_COLORS[s])} onClick={() => setActiveSection(s)}>{s}</button>
        ))}
      </div>

      {mode === "practice" ? (
        <>
          {score.answered > 0 && (
            <div style={{ textAlign:"center", fontSize:13, fontWeight:700, color:"#fbbf24", marginBottom:14 }}>
              Score: {score.correct} / {score.answered} answered correctly
            </div>
          )}
          {filteredQuestions.map((item, i) => (
            <QuizQuestion key={i} item={item} index={i} onAnswer={handleAnswer} />
          ))}
        </>
      ) : (
      <>
      {/* ── Bookmark filter ── */}
      <div style={{ textAlign:"center", margin:"0 0 14px" }}>
        <button style={S.chip(showBookmarkedOnly, "#db2777")} onClick={() => setShowBookmarkedOnly(v => !v)}>
          🔖 {showBookmarkedOnly ? "Showing Bookmarked" : "Bookmarked"} ({bookmarks.length})
        </button>
      </div>

      {/* ── Quick reference table ── */}
      <div style={S.card}>
        <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginBottom:12 }}>📋 Quick Reference Table</div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:"2px solid rgba(255,255,255,0.15)" }}>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>#</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Section</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Topic</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={i} style={{ borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                  <td style={{ padding:"7px 8px", color:"#64748b" }}>{i + 1}</td>
                  <td style={{ padding:"7px 8px" }}><span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span></td>
                  <td style={{ padding:"7px 8px" }}>
                    <a
                      href={`#concept-${i}`}
                      style={{ color:"#38bdf8", textDecoration:"underline", fontWeight:600 }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#7dd3fc"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#38bdf8"; }}
                    >
                      {c.title}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Detailed concept cards ── */}
      {filtered.map((c, i) => {
        const isBookmarked = bookmarks.includes(c.title);
        const isSpeaking = speakingIdx === i;
        return (
        <div key={i} id={`concept-${i}`} style={S.card}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, flexWrap:"wrap" }}>
            <span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span>
            <div style={{ display:"flex", gap:6 }}>
              <button
                title={isSpeaking ? "Stop reading" : "Read aloud"}
                onClick={() => speakConcept(c, i)}
                style={S.iconBtn(isSpeaking ? "#2563eb" : "rgba(37,99,235,0.22)")}
              >{isSpeaking ? "⏹" : "🔊"}</button>
              <button
                title={isBookmarked ? "Remove bookmark" : "Bookmark"}
                onClick={() => toggleBookmark(c.title)}
                style={S.iconBtn(isBookmarked ? "#db2777" : "rgba(219,39,119,0.22)")}
              >{isBookmarked ? "🔖" : "📌"}</button>
              <button
                title="Share"
                onClick={() => shareConcept(c, i)}
                style={S.iconBtn("rgba(255,255,255,0.12)")}
              >↗</button>
            </div>
          </div>
          <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginTop:8 }}>{i + 1}. {c.title}</div>
          <div style={{ fontSize:13.5, color:"#cbd5e1", lineHeight:1.7, marginTop:8 }}>{c.explanation}</div>
          <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", marginTop:12, marginBottom:6, letterSpacing:0.4 }}>EXAMPLE / WORKED CALCULATION</div>
          <pre style={S.pre}><code>{c.example}</code></pre>
        </div>
        );
      })}
      </>
      )}
    </div>
  );
}
